import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { customGenericStyles } from '@rsos/base-components/Select/Select.styles';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { AIR_AMBULANCE_STANDBY } from '@rsos/capstone/src/constants/capabilities';
import { Box, Text, Select, Button } from '@rsos/components/capstone/base';
import useHasCapability from '@rsos/hooks/useHasCapability';
import {
  selectExistingRequest,
  AirAmbulanceRequest,
} from '@rsos/reduxDataStore';
import type { SelectOption, FormField } from '../../types';
import { Label, ButtonContainer } from '../AirAmbulanceFormStandalone.styles';
import {
  REQUEST_REASONS,
  FORM_FIELDS,
  DEFAULT_BUTTON_OPTIONS,
  REQUEST_REASON,
  LAUNCH_TYPES,
} from '../constants';
import { REQUIRED_ERROR } from '../validation';
import { RadioButtonTitle } from './RequestSection.styles';

interface StyledButtonProps {
  $isTest?: boolean;
  $isSelected?: boolean;
  btnType?: string;
}

const StyledButton = styled(Button)<StyledButtonProps>`
  ${({ theme, $isTest, $isSelected, btnType }) =>
    $isTest && $isSelected && btnType === 'primary'
      ? `
          background-color: ${theme.colors.caution};
          &:hover {
            filter: brightness(0.8);
            background-color: ${theme.colors.caution};
          }
        `
      : ''}
`;

interface FormValues {
  request_reason: string | SelectOption;
  launch_type: string;
  multiple_units_requested: string;
  air_service_declined: string;
  air_service_responding: string;
  [key: string]: string | SelectOption;
}

interface TouchedFields {
  request_reason?: boolean;
  [key: string]: boolean | undefined;
}

interface RequestSectionProps {
  values: FormValues;
  touched: TouchedFields;
  setFieldValue: (field: string, value: unknown) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
}

interface SelectControlState {
  selectProps: {
    theme: {
      colors: {
        lightDanger: string;
        heavyLineOutline: string;
      };
    };
  };
  isDisabled: boolean;
}

const RequestSection = ({
  values,
  touched,
  setFieldValue,
  setFieldTouched,
}: RequestSectionProps): JSX.Element => {
  const hasGmrStandbyCapability = useHasCapability(
    AIR_AMBULANCE_STANDBY,
  )?.hasCapability;
  const existingRequest: AirAmbulanceRequest | null = useSelector(
    selectExistingRequest,
  );
  const hasExistingRequest = existingRequest !== null;
  const hasError = useMemo(() => {
    return touched.request_reason && values.request_reason === 'Select';
  }, [touched, values]);
  const styles = useMemo(() => {
    const baseStyles = customGenericStyles as Record<string, unknown>;
    return {
      ...baseStyles,
      control: (
        base: Record<string, unknown>,
        state: SelectControlState,
      ): Record<string, unknown> => ({
        ...((
          baseStyles.control as (
            base: Record<string, unknown>,
            state: SelectControlState,
          ) => Record<string, unknown>
        )?.(base, state) || {}),
        borderColor: hasError
          ? state.selectProps.theme.colors.lightDanger
          : state.selectProps.theme.colors.heavyLineOutline,
        opacity: state.isDisabled ? 0.5 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: 'auto',
      }),
    };
  }, [hasError]);
  return (
    <>
      <Box pb={3}>
        <Label>
          <Text size="small" color="primaryText">
            Request Reason*
          </Text>
        </Label>
        {/* NOTE: classNamePrefix is used to proivde a better identifer to playwright tests for the container of the react-select compnent */}
        <Select
          name="request_reason"
          classNamePrefix="request_reason"
          options={REQUEST_REASONS}
          placeholder="Please select"
          isClearable={false}
          isSearchable={false}
          onChange={(selectedOption: SelectOption | null) => {
            setFieldTouched(REQUEST_REASON, true);
            setFieldValue(REQUEST_REASON, selectedOption);
          }}
          onBlur={() => setFieldTouched(REQUEST_REASON, true)}
          value={values.request_reason}
          styles={styles}
          isDisabled={hasExistingRequest}
        />
        {hasError && <ErrorWrapper error={REQUIRED_ERROR} />}
      </Box>

      {FORM_FIELDS.map(
        ({ label, field, options = DEFAULT_BUTTON_OPTIONS }: FormField) => {
          const filteredOptions =
            field === 'launch_type' && !hasGmrStandbyCapability
              ? options.filter(option => option !== LAUNCH_TYPES.STANDBY)
              : options;
          return (
            <Box pb={4} key={field}>
              <Label>
                <RadioButtonTitle hasError={!values[field]} size="small">
                  {label}
                </RadioButtonTitle>
              </Label>
              <ButtonContainer horizontal>
                {filteredOptions?.map((option: string) => (
                  <StyledButton
                    key={option}
                    type="button"
                    btnType={values[field] === option ? 'primary' : 'tertiary'}
                    onClick={() => setFieldValue(field, option)}
                    grow
                    disabled={hasExistingRequest}
                    $isTest={
                      field === 'launch_type' && option === LAUNCH_TYPES.TEST
                    }
                    $isSelected={values[field] === option}
                  >
                    {option}
                  </StyledButton>
                ))}
              </ButtonContainer>
            </Box>
          );
        },
      )}
    </>
  );
};

export default RequestSection;
